import styled from "@emotion/styled";
import { ChevronLeft, ChevronRight, KeyboardArrowDown } from "@mui/icons-material";
import StorageIcon from "@mui/icons-material/Storage";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CircularProgress,
  circularProgressClasses,
  Container,
  IconButton,
  LinearProgress,
  linearProgressClasses,
  Link,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  AlertIcon,
  CalenderIcon,
  InfoBlueIcon,
  TickWithCircle,
  TimerIconMd
} from "../../assets/icons";
import {
  file
} from "../../assets/images";
import { config } from "../../config";
import BillingCycleModal from "../../modals/BillingCycleModal";
import CancelSubscription from "../../modals/CancelSubscription";
import CancellationFlow from "../../modals/subscription/CancellationFlow";
import ChargebeeService from "../../services/chargebee.service";
import UsageService from "../../services/usage.service";
import theme from "../../theme";
import { SubscriptionData } from "../../types/chargebee";
import { formatDate, formatPlanName } from "../../utils/common.util";
// import { CleverTapService } from "../../utils/clevertap.utils";
import { SubscriptionItemProp } from "../../types/auth";
import { validateUserPermissions } from "../../utils/common.util";
import { decodeMetadataToken } from "../../utils/token.utils";
import PricingSectionV2Sale from "./pricing/PricingSectionV2Sale";
import { ALL_IN_ONE_PRICES } from "./pricing/pricingData";
import MixpanelService from "../../services/mixpanel.service";

const getStaticAddonByItemPriceId = (itemPriceId: string) => {
  if (itemPriceId?.includes("NewAddon1-USD-Monthly") || itemPriceId?.includes("NewAddon1-USD-Yearly"))
    return { title: "New Addon 1", description: "Add-on feature description." };
  if (itemPriceId?.includes("NewAddon2-USD-Monthly") || itemPriceId?.includes("NewAddon2-USD-Yearly"))
    return { title: "New Addon 2", description: "Add-on feature description." };
  return null;
};

// Helper function to get usage color based on percentage
const getUsageColor = (percentage: number) => {
  if (percentage > 100) return "#E44258"; // Red for over 100%
  if (percentage >= 61) return "#FFA500"; // Yellow/Orange for 61-100%
  return "#178EFC"; // Blue for under 60%
};

const BorderLinearProgressGreen = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'usagePercentage'
})<{ usagePercentage?: number }>(({ theme, usagePercentage = 0 }) => ({
  height: 8,
  borderRadius: 18,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#DDE0E5", // Grey background
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 16,
    backgroundColor: getUsageColor(usagePercentage),
  },
}));


const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(18px)",
      top: "2px",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    top: "2px",
    left: "2px",
    "&.Mui-checked": {
      transform: "translateX(17px)",
      color: "#fff",
      top: "2px",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#22B060",
        // ...theme.applyStyles('dark', {
        //     backgroundColor: '#177ddc',
        // }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 18,
    height: 18,
    borderRadius: 18,
    transition: "200ms ease all",
  },
  "& .MuiSwitch-track": {
    borderRadius: 18,
    opacity: 1,
    backgroundColor: "#22B060",
    boxSizing: "border-box",
    // ...theme.applyStyles('dark', {
    //     backgroundColor: 'rgba(255,255,255,.35)',
    // }),
  },
}));

interface SubscriptionDetailsProps {
  navigate: (path: string) => void;
  state?: {
    subscriptionData?: SubscriptionData;
    scrollToPricing?: boolean;
  };
}

interface Tier {
  starting_unit: number;
  ending_unit?: number;
  price: number;
}

// Add this helper component above the SubscriptionDetails component
interface AddonButtonProps {
  isTrialMode: boolean;
  isPausedPlan?: boolean;
  addonName: string;
  addonPrice: number;
  onClick: () => void;
}

const AddonButton: React.FC<AddonButtonProps> = ({
  isTrialMode,
  isPausedPlan = false,
  addonName,
  addonPrice,
  onClick,
}) => {
  const disabled = isTrialMode || isPausedPlan;
  return (
    <Button
      variant="contained"
      color="primary"
      sx={{
        textTransform: "capitalize",
        borderRadius: "4px",
        px: "12px",
        height: "32px",
      }}
      onClick={() => {
        MixpanelService.track("ua_addon_add_to_plan_clicked", {
          addon_name: addonName,
          addon_price: addonPrice,
        });
        onClick();
      }}
      disabled={disabled}
    >
      {isTrialMode ? "Upgrade to Paid Plan" : "Add to Plan"}
    </Button>
  );
};

// SubscriptionFeatures component to display features based on subscription type
const SubscriptionFeatures = ({ planType, quantity, storageMax }: {
  planType: 'trial' | 'automated' | 'live' | 'all-in-one',
  quantity: number | string | undefined,
  storageMax?: number
}) => {
  // Common feature for all plans
  const attendeesFeature = (
    <Box display="flex" alignItems="center" gap={1} mb={2.25}>
      <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
      <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
        {`Up to ${quantity} attendees`}
      </Typography>
    </Box>
  );

  // Features for Free Trial plan
  const trialFeatures = [
    "Unlimited live & automated webinars",
    "Turn replays into automated webinars",
    "Polls, CTAs, and chat prompts",
    "Priority support & success onboarding",
    "Marketing & CRM Tool Integrations"
  ];

  // Features for Automated plan
  const automatedFeatures = [
    "Unlimited automated events",
    "Just-in-time scheduling",
    "Preloaded + live chats & real-time replays",
    "Automated, segmented email campaigns",
    "Data export & analytics"
  ];

  // Features for Live plan
  const liveFeatures = [
    "Unlimited live webinars",
    "Polls, Q&A, and emoji reactions",
    "Share offers, files & links",
    "Custom landing pages + smart scheduling",
    "Marketing & CRM Tool Integrations"
  ];

  // Features for All-in-One plan (same as trial plan for now)
  const allInOneFeatures = [
    "Unlimited live & automated webinars",
    "Turn replays into automated webinars",
    "Polls, CTAs, and chat prompts",
    "Priority support & success onboarding",
    "Marketing & CRM Tool Integrations"
  ];

  // Select the appropriate features based on plan type
  const features = {
    'trial': trialFeatures,
    'automated': automatedFeatures,
    'live': liveFeatures,
    'all-in-one': allInOneFeatures
  }[planType];

  // Split the features into two columns
  const leftColumnFeatures = features.slice(0, 3);
  const rightColumnFeatures = features.slice(3);

  return (
    <Grid container spacing={{ xs: 0, sm: 2 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        {leftColumnFeatures.map((feature, index) => (
          typeof feature === 'string' ? (
            <Box key={index} display="flex" alignItems="center" gap={1} mb={2.25}>
              <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
              <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                {feature}
              </Typography>
            </Box>
          ) : feature
        ))}
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        {rightColumnFeatures.map((feature, index) => (
          typeof feature === 'string' ? (
            <Box key={index} display="flex" alignItems="center" gap={1} mb={2.25}>
              <Box minWidth={"24px"} display="flex"><TickWithCircle /></Box>
              <Typography variant="body2" color="#333333" mt="2px" fontSize="min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)">
                {feature}
              </Typography>
            </Box>
          ) : feature
        ))}
      </Grid>
    </Grid>
  );
};

const SubscriptionDetails = ({ navigate, state }: SubscriptionDetailsProps) => {
  const { subscriptionData, scrollToPricing } = state || {};
  const isYearlyPlan = subscriptionData?.subscription?.billingPeriodUnit === "year";
  const isPausedPlan =
    subscriptionData?.subscription?.status === "paused" ||
    subscriptionData?.subscription_items?.find((item) => item.itemType === "plan")?.itemPriceId
      ?.toLowerCase()
      ?.includes("subscription-pause");

  // Security: Validate user permissions before allowing access to subscription details
  React.useEffect(() => {
    try {
      const permissionCheck = validateUserPermissions('OWNER');
      if (!permissionCheck.isValid) {
        toast.error(permissionCheck.error || 'Access denied: Only account owners can view subscription details');
        navigate('/dashboard/subscription');
        return;
      }
    } catch (error) {
      console.error('Error validating subscription details permissions:', error);
      toast.error('Error validating permissions');
      navigate('/dashboard/overview');
    }
  }, [navigate]);

  // Scroll to pricing section when navigating from banner
  React.useEffect(() => {
    if (scrollToPricing) {
      // Use a retry mechanism to ensure the component has rendered
      const scrollToPricingSection = () => {
        const pricingSection = document.getElementById("pricing-section");
        if (pricingSection) {
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            pricingSection.scrollIntoView({ behavior: "smooth", block: "start" });
          });
          return true;
        }
        return false;
      };

      // Wait a bit for initial render, then try
      const initialDelay = setTimeout(() => {
        if (!scrollToPricingSection()) {
          // If not found, retry with interval
          const retryInterval = setInterval(() => {
            if (scrollToPricingSection()) {
              clearInterval(retryInterval);
            }
          }, 200);

          // Clear interval after 3 seconds if still not found
          setTimeout(() => clearInterval(retryInterval), 3000);
        }
      }, 300);

      return () => {
        clearTimeout(initialDelay);
      };
    }
  }, [scrollToPricing, subscriptionData]);

  const entitlements = decodeMetadataToken()?.companies?.[0]?.entitlements;
  // Move all state declarations to the top
  const [renewelPaused, setRenewelPaused] = useState(false);
  const [cancelSubscriptionModal, setCancelSubscriptionModalOpen] = useState(false);
  const [isCancelModalOpen, setCancelModal] = useState(false);
  const [isBillingCycleModalOpen, setIsBillingCycleModalOpen] = useState(false);
  const [cancelAddonModal, setCancelAddonModal] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liveAttendeesUsageCount, setLiveAttendeesUsageCount] = useState<number>(0);
  const [participantsUsageCount, setParticipantsUsageCount] = useState<number>(0);
  const [liveParticipantsUsageCount, setLiveParticipantsUsageCount] = useState<number>(0);
  const [automatedParticipantsUsageCount, setAutomatedParticipantsUsageCount] = useState<number>(0);
  const [storageUsageValueGB, setStorageUsageValueGB] = useState<number>(0);
  const [storageUsageValue, setStorageUsageValue] = useState<number>(0);
  const [storageUsageUnit, setStorageUsageUnit] = useState<string>("GB");
  const [plans, setPlans] = useState<any[]>([]);
  const [isAnnual, setIsAnnual] = useState(true);
  const [automatedGoldAttendees, setAutomatedGoldAttendees] = useState("500");
  const [allInOneAttendees, setAllInOneAttendees] = useState("500");
  const [liveAttendeesMaxCount, setLiveAttendeesMaxCount] = useState<number>(1);
  const [participantsMaxCount, setParticipantsMaxCount] = useState<number>(1);
  const [storageUsageMaxCount, setStorageUsageMaxCount] = useState<number>(1);
  const [selectedAddonToCancel, setSelectedAddonToCancel] = useState<string | null>(null);
  const [isLivePlan, setIsLivePlan] = useState<boolean>(false);
  const [isAutomatedPlan, setIsAutomatedPlan] = useState<boolean>(false);
  const [isLiveAutomatedPlan, setIsLiveAutomatedPlan] = useState<boolean>(false);
  const [isFreeTrialPlan, setIsFreeTrialPlan] = useState<boolean>(false);
  const [selectedMonths, setSelectedMonths] = useState<number>(0);
  const [usageDateRange, setUsageDateRange] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [usageCycles, setUsageCycles] = useState<Array<{
    months: number;
    dateRange: string;
    termStart: number;
    termEnd: number;
    hasOverage: boolean;
    isCurrent?: boolean;
  }>>([]);

  const trackUsagePeriodChanged = (
    source: "arrows" | "dropdown",
    targetMonthsIndex: number
  ) => {
    const cycle = usageCycles.find((c) => c.months === targetMonthsIndex);
    if (!cycle) return;
    MixpanelService.track("ua_subscription_usage_period_changed", {
      "Navigate billing period": source,
      period: cycle.isCurrent ? "current" : "previous",
      date_range: cycle.dateRange,
    });
  };

  // Helper function to determine if sale should be shown based on subscription conditions
  const shouldShowSale = useMemo(() => {
    const metadata = decodeMetadataToken();
    // Don't show sale banners to users with parentCompanyId
    if (metadata?.companies?.[0]?.parentCompanyId) {
      return false;
    }
    return config.isSaleLive && (
      subscriptionData?.subscription?.status === "in_trial" ||
      subscriptionData?.subscription_items?.some(item => item.itemPriceId?.includes("free-trial-plan")) ||
      subscriptionData?.subscription?.billingPeriodUnit === "month"
    );
  }, [subscriptionData]);

  // Group all useEffect hooks together at the top
  useEffect(() => {
    if (!subscriptionData) {
      console.error("No subscription data provided");
      navigate("/subscription"); // Redirect back if no data
    }
  }, [subscriptionData, navigate]);

  useEffect(() => {
  }, [liveAttendeesMaxCount]);

  useEffect(() => {
  }, [participantsMaxCount]);

  useEffect(() => {
  }, [storageUsageMaxCount]);

  // Setting plan types
  useEffect(() => {
    if (subscriptionData?.subscription_items) {
      const currentPlanId = subscriptionData.subscription_items.find(
        (item) => item.itemType === "plan"
      )?.itemPriceId || "";

      // Set isLiveAutomatedPlan to true if plan contains "All-In-One"
      setIsLiveAutomatedPlan(
        currentPlanId.includes("All-In-One")
      );

      // Set isAutomatedPlan to true if plan contains "Automated-Gold"
      setIsAutomatedPlan(
        currentPlanId.includes("Automated-Gold")
      );

      // Set isLivePlan to true if plan contains "Live-Webinars"
      setIsLivePlan(
        currentPlanId.includes("Live-Webinars")
      );

      // Set isFreeTrialPlan to true if plan contains "free-trial-plan"
      setIsFreeTrialPlan(
        currentPlanId.includes("free-trial-plan")
      );
    }
  }, [subscriptionData]);

  // Helper function to format date range
  const formatDateRange = (termStart: number, termEnd: number, months: number) => {
    if (!termStart || !termEnd) return "";
    const startDate = new Date(termStart * 1000);
    const endDate = new Date(termEnd * 1000);
    
    // For end date, subtract 1 day since termEnd represents the start of the next period
    // We want to show the last day of the current period
    const actualEndDate = new Date(endDate);
    actualEndDate.setDate(actualEndDate.getDate() - 1);
    
    const formatDate = (date: Date) => {
      const month = date.toLocaleString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    };
    
    const startFormatted = formatDate(startDate);
    const endFormatted = formatDate(actualEndDate);
    const year = actualEndDate.getFullYear();
    
    // Use en dash (–) instead of hyphen (-) to match the design
    return `${startFormatted} – ${endFormatted}, ${year}`;
  };

  // Generate usage cycles list
  const generateUsageCycles = async () => {
    if (!subscriptionData?.subscription) return;
    
    // For new accounts or trial subscriptions, use fallback dates
    // Priority: currentTermStart/currentTermEnd > trialStartDate > nextBillingAt/planExpireDate > current date
    const currentTermStart = subscriptionData.subscription.currentTermStart;
    const currentTermEnd = subscriptionData.subscription.currentTermEnd;
    const trialStartDate = subscriptionData.subscription.trialStartDate;
    const nextBillingAt = subscriptionData.subscription.nextBillingAt;
    const planExpireDate = subscriptionData.subscription.planExpireDate;
    const status = subscriptionData.subscription.status;

    // Chargebee can have `nextBillingAt` adjusted without `currentTermEnd` updating immediately (and vice-versa).
    // For UI ranges, we cap by the EARLIEST known "term end" candidate to avoid showing ranges past renewal.
    const resolveEffectiveTermEnd = () => {
      const candidates = [currentTermEnd, nextBillingAt, planExpireDate].filter(
        (v): v is number => typeof v === 'number' && v > 0
      );
      return candidates.length > 0 ? Math.min(...candidates) : undefined;
    };
    
    // Determine base start date with fallbacks
    let baseTermStart: number;
    let baseTermEnd: number;
    
    if (currentTermStart && currentTermEnd) {
      // Normal subscription with term dates
      baseTermStart = currentTermStart;
      baseTermEnd = resolveEffectiveTermEnd() ?? currentTermEnd;
    } else if (trialStartDate) {
      // Trial subscription - use trial start date
      // Free trial is 7 days, so calculate 7 days ahead
      baseTermStart = trialStartDate;
      const trialEndDate = new Date(trialStartDate * 1000);
      trialEndDate.setDate(trialEndDate.getDate() + 7);
      baseTermEnd = Math.floor(trialEndDate.getTime() / 1000);
    } else if (status === 'in_trial' && (nextBillingAt || planExpireDate)) {
      // Trial account without trialStartDate - calculate from nextBillingAt/planExpireDate
      // Free trial is 7 days, so calculate backwards from expiry date
      let expiryDate = resolveEffectiveTermEnd();
      if (!expiryDate) {
        expiryDate = nextBillingAt || planExpireDate;
      }
      const expiry = new Date(expiryDate * 1000);
      // Calculate trial start (7 days before expiry)
      const trialStart = new Date(expiry);
      trialStart.setDate(trialStart.getDate() - 7);
      baseTermStart = Math.floor(trialStart.getTime() / 1000);
      baseTermEnd = expiryDate;
    } else {
      // New account with no dates - use current date
      const now = Math.floor(Date.now() / 1000);
      baseTermStart = now;
      const oneMonthLater = new Date(now * 1000);
      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
      baseTermEnd = Math.floor(oneMonthLater.getTime() / 1000);
    }
    
    if (!baseTermStart || !baseTermEnd) return;
    
    // Always use monthly calculation regardless of billing period unit
    // This allows users to view monthly usage even for yearly subscriptions
    console.log('generateUsageCycles - baseTermStart:', new Date(baseTermStart * 1000).toISOString());
    console.log('generateUsageCycles - baseTermEnd:', new Date(baseTermEnd * 1000).toISOString());
    
    // Generate cycles for current + 3 past periods (always monthly) = 4 total months
    // For new accounts or trial accounts, only show current period
    // IMPORTANT: Usage months are aligned to subscription start DAY, not calendar months
    // If subscription started on July 13, all usage months start on the 13th of each month
    // Use `currentTermEnd` as the anchor for month boundaries.
    // Reason: `currentTermStart` can stay on the old day when billing is adjusted in Chargebee,
    // but the UI ranges should align to the updated renewal boundary.
    const boundaryAnchorDate = new Date(baseTermEnd * 1000);
    // Use UTC day-of-month since Chargebee timestamps are Unix (UTC) seconds
    const subscriptionStartDay = boundaryAnchorDate.getUTCDate(); // Boundary day-of-month (e.g., 1)
    const nowTimestamp = Math.floor(Date.now() / 1000);
    
    const isNewAccount = !currentTermStart && !currentTermEnd && !trialStartDate;
    const isTrialAccount = status === 'in_trial';
    const maxCycles = (isNewAccount || isTrialAccount) ? 1 : 4; // Only show current period for new/trial accounts
    
    const cycles: Array<{
      months: number;
      dateRange: string;
      termStart: number;
      termEnd: number;
      hasOverage: boolean;
      isCurrent?: boolean;
    }> = [];
    
    if (isTrialAccount) {
      // For trial accounts, use actual trial period dates (7 days, not monthly)
      const dateRange = formatDateRange(baseTermStart, baseTermEnd, 0);
      cycles.push({
        months: 0,
        dateRange,
        termStart: baseTermStart,
        termEnd: baseTermEnd,
        hasOverage: false,
        isCurrent: true
      });
    } else {
      // For regular subscriptions, keep the monthly dropdown anchored around "now" (so it never goes empty),
      // but CAP the selectable range by Chargebee's `currentTermEnd` when it is edited/shortened.
      //
      // If the subscription already ended, we anchor on the last second inside the term.
      const effectiveNowTimestamp = Math.min(nowTimestamp, baseTermEnd - 1);
      const effectiveNow = new Date(effectiveNowTimestamp * 1000);

      // Find the current cycle (the one containing effectiveNowTimestamp)
      let currentCycleOffset = 0;
      let foundCurrentCycle = false;

      for (let checkOffset = 0; checkOffset < 12; checkOffset++) {
        const checkMonthStart = new Date(
          Date.UTC(
            effectiveNow.getUTCFullYear(),
            effectiveNow.getUTCMonth() - checkOffset,
            subscriptionStartDay,
            0,
            0,
            0
          )
        );
        const lastDayOfCheckMonth = new Date(
          Date.UTC(checkMonthStart.getUTCFullYear(), checkMonthStart.getUTCMonth() + 1, 0)
        ).getUTCDate();
        if (subscriptionStartDay > lastDayOfCheckMonth) {
          checkMonthStart.setUTCDate(lastDayOfCheckMonth);
        }

        let checkTermStart = Math.floor(checkMonthStart.getTime() / 1000);

        const nextCheckMonthStart = new Date(
          Date.UTC(
            effectiveNow.getUTCFullYear(),
            effectiveNow.getUTCMonth() - (checkOffset - 1),
            subscriptionStartDay,
            0,
            0,
            0
          )
        );
        const lastDayOfNextCheckMonth = new Date(
          Date.UTC(nextCheckMonthStart.getUTCFullYear(), nextCheckMonthStart.getUTCMonth() + 1, 0)
        ).getUTCDate();
        if (subscriptionStartDay > lastDayOfNextCheckMonth) {
          nextCheckMonthStart.setUTCDate(lastDayOfNextCheckMonth);
        }
        let checkTermEnd = Math.floor(nextCheckMonthStart.getTime() / 1000);

        // Cap by subscription term end so we don't go beyond Chargebee's currentTermEnd
        checkTermEnd = Math.min(checkTermEnd, baseTermEnd);

        if (checkTermStart <= effectiveNowTimestamp && effectiveNowTimestamp < checkTermEnd) {
          currentCycleOffset = checkOffset;
          foundCurrentCycle = true;
          break;
        }
      }

      if (!foundCurrentCycle) {
        // Fallback: use the current calendar month aligned to subscriptionStartDay
        currentCycleOffset = 0;
      }

      for (let i = 0; i < maxCycles; i++) {
        const monthsBack = currentCycleOffset + i;

        const usageMonthStart = new Date(
          Date.UTC(
            effectiveNow.getUTCFullYear(),
            effectiveNow.getUTCMonth() - monthsBack,
            subscriptionStartDay,
            0,
            0,
            0
          )
        );
        const lastDayOfMonth = new Date(
          Date.UTC(usageMonthStart.getUTCFullYear(), usageMonthStart.getUTCMonth() + 1, 0)
        ).getUTCDate();
        if (subscriptionStartDay > lastDayOfMonth) {
          usageMonthStart.setUTCDate(lastDayOfMonth);
        }

        const nextUsageMonthStart = new Date(
          Date.UTC(
            effectiveNow.getUTCFullYear(),
            effectiveNow.getUTCMonth() - (monthsBack - 1),
            subscriptionStartDay,
            0,
            0,
            0
          )
        );
        const lastDayOfNextMonth = new Date(
          Date.UTC(nextUsageMonthStart.getUTCFullYear(), nextUsageMonthStart.getUTCMonth() + 1, 0)
        ).getUTCDate();
        if (subscriptionStartDay > lastDayOfNextMonth) {
          nextUsageMonthStart.setUTCDate(lastDayOfNextMonth);
        }

        let termStart = Math.floor(usageMonthStart.getTime() / 1000);
        let termEnd = Math.floor(nextUsageMonthStart.getTime() / 1000);

        // Cap by subscription term end so we don't go beyond Chargebee's currentTermEnd
        termEnd = Math.min(termEnd, baseTermEnd);

        // Only show cycles that have started (past/current only)
        if (termStart <= nowTimestamp && termStart < termEnd) {
          const dateRange = formatDateRange(termStart, termEnd, cycles.length);
          const isCurrent = termStart <= nowTimestamp && nowTimestamp < termEnd;

          cycles.push({
            months: cycles.length,
            dateRange,
            termStart,
            termEnd,
            hasOverage: false,
            isCurrent
          });
        }
      }
    }
    
    // Find the current cycle index for initial selection
    let currentCycleIndex = 0;
    for (let i = 0; i < cycles.length; i++) {
      if (cycles[i].isCurrent) {
        currentCycleIndex = i;
        break;
      }
    }
    
    // Set cycles immediately so dropdown appears quickly
    setUsageCycles(cycles);
    
    // Set initial date range for current period to avoid "Loading..." state
    const actualCurrentCycle = cycles[currentCycleIndex];
    if (actualCurrentCycle) {
      setUsageDateRange(actualCurrentCycle.dateRange);
      setSelectedMonths(actualCurrentCycle.months);
    } else if (cycles.length > 0) {
      setUsageDateRange(cycles[0].dateRange);
    }
    
    // Fetch overage data in background (non-blocking) - only for current period initially
    // This avoids making 4 API calls on page load
    const currentCycle = cycles[currentCycleIndex];
    if (currentCycle) {
      // Check overage for current period only (non-blocking)
      UsageService.fetchUsage(
        currentCycle.months,
        currentCycle.termStart,
        currentCycle.termEnd
      ).then((usageResult) => {
        if (usageResult?.data) {
          const hasOverage = usageResult.data.some((el: any) => {
            if (el.max_count > 0 && el.event_count > el.max_count) {
              return true;
            }
            if (el.event_id === 'attendee_registered' && el.automated_attendees_count) {
              const maxAutomated = el.max_count || 0;
              return el.automated_attendees_count > maxAutomated;
            }
            return false;
          });
          
          // Update only the current cycle's overage status
          setUsageCycles(prevCycles => 
            prevCycles.map(cycle => 
              cycle.isCurrent ? { ...cycle, hasOverage } : cycle
            )
          );
        }
      }).catch(() => {
        // Silently fail - overage check is not critical for UI
      });
    }
    
    // Optionally: Fetch overage for other periods in background (non-blocking)
    // This can be done lazily when user opens dropdown or hovers over a period
  };

  // Fetching data
  useEffect(() => {
    const fetchUsageData = async () => {
      // Don't fetch if usageCycles is not yet populated
      if (!usageCycles || usageCycles.length === 0) {
        console.log('Waiting for usage cycles to be generated...');
        return;
      }
      
      setUsageLoading(true); // Show skeleton while fetching
      try {
        // Use dates from the selected cycle in usageCycles array (single source of truth)
        // This ensures consistency with the cycles displayed in the dropdown
        const selectedCycle = usageCycles.find(cycle => cycle.months === selectedMonths);
        
        if (!selectedCycle) {
          console.warn('Cannot fetch usage data: selected cycle not found', {
            selectedMonths,
            availableCycles: usageCycles.map(c => c.months)
          });
          setUsageLoading(false);
          return;
        }
        
        const termStart = selectedCycle.termStart;
        const termEnd = selectedCycle.termEnd;
        
        // Set date range immediately to avoid "Loading..." state
        setUsageDateRange(selectedCycle.dateRange);
        
        console.log(`fetchUsageData - selectedMonths: ${selectedMonths}`, {
          unixTermStart: termStart * 1000,
          unixTermEnd: termEnd * 1000,
          termStart: new Date(termStart * 1000).toISOString(),
          termEnd: new Date(termEnd * 1000).toISOString(),
          dateRange: selectedCycle.dateRange
        });
        
        // Only make API call if we have valid dates
        if (!termStart || !termEnd) {
          console.warn('Cannot fetch usage data: missing termStart or termEnd');
          setUsageLoading(false);
          return;
        }
        
        // Call API with calculated dates (frontend is source of truth)
        const usageResult = await UsageService.fetchUsage(
          selectedMonths,
          termStart,
          termEnd
        );
        
        setUsageData(usageResult.data);
        console.log("subscriptionData usage data", subscriptionData);
        
        if (usageResult && usageResult.data)
          usageResult.data.map((el: any) => {
            if (el.event_id === "attendee_joined") {
              setLiveParticipantsUsageCount(el.event_count);
              setLiveAttendeesMaxCount(el.max_count);
            }
            if (el.event_id === "attendee_registered") {
              setAutomatedParticipantsUsageCount(el.automated_attendees_count);
               setParticipantsMaxCount(el.max_count);
            }
            if (el.event_id === "storage_consumed") {
              setStorageUsageValue(el.event_count);
              const unit = el.event_count < 1 ? "MB" : "GB";
              const value =
                el.event_count < 1 ? el.event_count * 1024 : el.event_count;
              setStorageUsageValue(value);
              setStorageUsageUnit(unit);
              setStorageUsageMaxCount(el.event_count < 1 ? el.max_count * 1024 : el.max_count);
            }
          });
      } catch (error) {
        setError((error as any).message);
      } finally {
        setUsageLoading(false); // Hide skeleton when done
      }
    };

    const fetchPlansData = async () => {
      try {
        const plansResult = await ChargebeeService.getPlans();
        setPlans(plansResult.data || []);
      } catch (error) {
        setError((error as any).message);
      }
    };

    Promise.all([fetchUsageData(), fetchPlansData()]).finally(() => {
      setPlanLoading(false);
      
      // Update initial user properties in Clevertap
      // if (subscriptionData?.subscription_items) {
      //   const currentPlan = subscriptionData.subscription_items.find(item => item.itemType === "plan");
      //   if (currentPlan) {
      //     const userData = decodeIdToken();
      //     if (userData) {
      //       const planName = formatPlanName(currentPlan.itemPriceId);
      //       const attendeeLimit = currentPlan.quantity || 1;
            
      //       CleverTapService.updateUserProperties({
      //         'Plan': planName,
      //         'Upgrade': false,
      //         'Downgrade': false,
      //         'Attendee Limit': attendeeLimit,
      //         'old-plan': false,
      //       });
      //     }
      //   }
      // }
    });
  }, [selectedMonths, subscriptionData, usageCycles]);

  // Generate usage cycles when subscription data is available
  useEffect(() => {
    if (subscriptionData?.subscription) {
      generateUsageCycles();
    }
  }, [subscriptionData]);


  const getStatusDisplay = (subscription: any) => {
    if (!subscription) return { text: "Unknown", color: "#666666" };

    switch (subscription.status) {
      case "active":
        return { text: "Active", color: "#178EFC" };
      case "cancelled":
        return { text: "Cancelled", color: "#FF4242" };
      case "in_trial":
        return { text: "Trial", color: "#22B060" };
      case "non_renewing":
        return { text: "Non-Renewing", color: "#FF4242" };
      default:
        return { text: subscription.status, color: "#666666" };
    }
  };

  const calculateNextBillingAmount = () => {
    if (!subscriptionData) return 0;

    let totalCents = 0;

    subscriptionData.subscription_items.forEach((item) => {
      totalCents += item.amount || 0;
    });

    return (totalCents / 100).toFixed(2);
  };

  function handleClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    event.preventDefault();
  }

  // Function to get pricing options from plans
  const getPricingOptions = () => {
    const automatedGoldMonthly = plans.find(
      (p) => p.id === "Automated-Gold-USD-Monthly"
    );
    const automatedGoldYearly = plans.find(
      (p) => p.id === "Automated-Gold-USD-Yearly"
    );
    const allInOneMonthly = plans.find(
      (p) => p.id === "All-In-One-USD-Monthly"
    );
    const allInOneYearly = plans.find((p) => p.id === "All-In-One-USD-Yearly");

    // Get unique attendee tiers
    const attendeeOptions =
      automatedGoldMonthly?.item_price.tiers
        .filter((tier: Tier) => tier.ending_unit)
        .map((tier: Tier) => tier.ending_unit!.toString()) || [];

    return {
      attendeeOptions,
      getPrice: (planId: string, attendees: number) => {
        const plan = plans.find((p) => p.id === planId);
        const tier = plan?.item_price.tiers.find(
          (t: Tier) =>
            attendees >= t.starting_unit &&
            (!t.ending_unit || attendees <= t.ending_unit)
        );
        return tier ? tier.price / 100 : 0; // Convert cents to dollars
      },
    };
  };

  const { attendeeOptions, getPrice } = getPricingOptions();

  // Toggle between monthly and annual
  const handlePlanToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAnnual(event.target.checked);
    // if (event.target.checked) {
    //   setIsBillingCycleModalOpen(true);
    // } else {
    //   setIsAnnual(false);
    // }
  };

  // Separate handlers for each plan
  const handleAutomatedGoldAttendees = (selected: string) => {
    setAutomatedGoldAttendees(selected);
  };

  const handleAllInOneAttendees = (selected: string) => {
    setAllInOneAttendees(selected);
  };

  const handleBillingCycleConfirm = () => {
    setIsAnnual(true);
    setIsBillingCycleModalOpen(false);
  }

  const addonsAvailableForPlan = useMemo(() => {
    const EVENT_PLAN_KEYS = ["50", "200", "500"];
    const FUNNEL_PLAN_KEYS = ["3000", "6000", "10000"];
    const quantity = subscriptionData?.subscription_items?.find((item) => item.itemType === "plan")?.quantity;
    const planKey = quantity != null ? String(quantity) : undefined;
    const planInfo =
      planKey && EVENT_PLAN_KEYS.includes(planKey)
        ? ALL_IN_ONE_PRICES.events[planKey as keyof typeof ALL_IN_ONE_PRICES.events]
        : planKey && FUNNEL_PLAN_KEYS.includes(planKey)
          ? ALL_IN_ONE_PRICES.funnels[planKey as keyof typeof ALL_IN_ONE_PRICES.funnels]
          : undefined;
    const addons = Array.isArray(planInfo?.addons) ? (planInfo?.addons ?? []) : [];
    return { easycrm: addons.includes("easycrm"), paidwebinars: addons.includes("paidwebinars") };
  }, [subscriptionData?.subscription_items]);

  // Add logic to handle the billing cycle change
  // Add this helper function to determine if the current plan is annual
  const isCurrentPlanAnnual = () => {
    const currentPlanId = subscriptionData?.subscription_items.find(
      (item) => item.itemType === "plan"
    )?.itemPriceId || "";

    return currentPlanId.includes("Yearly");
  };

  // Add this helper function to get the correct addon suffix
  const getAddonSuffix = () => {
    return isCurrentPlanAnnual() ? "USD-Yearly" : "USD-Monthly";
  };

  // Add this method before handleUpgradePlan
  const handleUnlockFullAccess = () => {
    // Get current plan ID without "Trial" in it
    const currentPlanId = subscriptionData?.subscription_items.find(
      (item) => item.itemType === "plan"
    )?.itemPriceId || "";

    // Extract plan details
    const planType = currentPlanId.includes("Automated-Gold") ? "Automated-Gold" : "All-In-One";
    const currency = "USD";
    const billingCycle = currentPlanId.includes("Yearly") ? "Yearly" : "Monthly";
    const quantity = subscriptionData?.subscription_items.find(
      (item) => item.itemType === "plan"
    )?.quantity || 200;

    // Create the proper item ID format
    const itemId = `${planType}-${currency}-${billingCycle}-${quantity}`;

    // Call the upgrade plan method with the formatted item ID
    handleUpgradePlan(itemId, true);
  };

  if (!subscriptionData) {
    return null;
  }

  const scrollToAddOns = () => {
    const addOnsSection = document.getElementById("available-add-ons");
    if (addOnsSection) {
      addOnsSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToUpgradePlan = () => {
    const pricingSection = document.getElementById("pricing-section");
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSupportTeamClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const intercom = (
      window as Window & { Intercom?: (command: "show") => void }
    ).Intercom;

    if (typeof intercom === "function") {
      console.info("[SubscriptionDetails] Opening Intercom from support link.");
      intercom("show");
      return;
    }

    console.warn("[SubscriptionDetails] Intercom is unavailable.");
    toast.info("Support chat is loading. Please try again in a moment.");
  };

  const getAllAddonItemPriceIdsToRemove = (items: SubscriptionItemProp[]): string[] =>
    items
      .filter((item) => item.itemType === 'addon' && item.itemPriceId)
      .map((item) => item.itemPriceId);
  // Update handleUpgradePlan to use selectedPlanId
  const handleUpgradePlan = async (itemId: string, isPlanUpgrade: boolean) => {
    
    // Extract units from itemId if it contains a number at the end
    const unitsMatch = itemId.match(/-(\d+)$/);
    const units = unitsMatch ? parseInt(unitsMatch[1]) : undefined;

    // Remove the units from itemId if present
    const baseItemId = unitsMatch ? itemId.slice(0, -(unitsMatch[0].length)) : itemId;
    
    try {
      const subscriptionId = subscriptionData?.subscription?.id;

      // For add-ons, append the appropriate suffix if needed (except for custom-domain)
      if (!isPlanUpgrade &&
        !itemId.includes('USD-Monthly') &&
        !itemId.includes('USD-Yearly') &&
        itemId !== "custom-domain") {
        itemId = `${itemId}-${getAddonSuffix()}`;
      }

      // Prepare parameters for the API call
      const params: any = {
        subscriptionId,
        planId: baseItemId,
      };

      // Only include units and trialEnd for plan upgrades
      if (isPlanUpgrade) {
        const finalUnits = units

        params.units = finalUnits;
        params.trialEnd = 0; // 0 to end trial immediately

        if(shouldShowSale) {
          if(params.planId.includes("USD-Yearly")) {
            params.couponId = config.yearlyCouponCode;
          } else {
            params.couponId = config.monthlyCouponCode;
          }
        }

        // Check if this is a downgrade by comparing current and new plan
        const currentPlan = subscriptionData?.subscription_items?.find(item => item.itemType === "plan")?.itemPriceId || '';
        const currentUnits = subscriptionData?.subscription_items?.find(item => item.itemType === "plan")?.quantity || 1;
        const isDowngrade = (finalUnits || 1) < currentUnits || 
                           ((finalUnits || 1) === currentUnits && baseItemId !== currentPlan);

        // if (isDowngrade) {
        //   // Track plan downgrade event
        //   const downgradeData = {
        //     'Current Plan': currentPlan,
        //     'New Plan': baseItemId,
        //     'Current Units': currentUnits,
        //     'New Units': finalUnits || 1,
        //     'Billing Cycle': baseItemId.includes('USD-Yearly') ? 'Yearly' : 'Monthly',
        //     'Page Title': document.title,
        //     'Page URL': window.location.href,
        //     'Timestamp': new Date().toISOString(),
        //   };
        //   CleverTapService.trackEvent('ew_downgraded', downgradeData);

        //   // Update user properties for downgrade
        //   const userData = decodeIdToken();
        //   if (userData) {
        //     const planName = formatPlanName(baseItemId);
        //     CleverTapService.updateUserProperties({
        //       'Plan': planName,
        //       'Upgrade': false,
        //       'Downgrade': true,
        //       'Attendee Limit': finalUnits || 1,
        //       'old-plan': currentPlan,
        //     });
        //   }
        // } else {
        //   // Track plan upgrade event
        //   const upgradeData = {
        //     'Current Plan': currentPlan,
        //     'New Plan': baseItemId,
        //     'Units': finalUnits || 1,
        //     'Billing Cycle': baseItemId.includes('USD-Yearly') ? 'Yearly' : 'Monthly',
        //     'Coupon Applied': params.couponId || 'None',
        //     'Page Title': document.title,
        //     'Page URL': window.location.href,
        //     'Timestamp': new Date().toISOString(),
        //   };
        //   CleverTapService.trackEvent('ew_user_upgraded', upgradeData);

        //   // Update user properties for upgrade
        //   const userData = decodeIdToken();
        //   if (userData) {
        //     const planName = formatPlanName(baseItemId);
        //     CleverTapService.updateUserProperties({
        //       'Plan': planName,
        //       'Upgrade': true,
        //       'Downgrade': false,
        //       'Attendee Limit': finalUnits || 1,
        //       'old-plan': currentPlan,
        //     });
        //   }
        // }
      }

      const itemsToRemove = getAllAddonItemPriceIdsToRemove(subscriptionData?.subscription_items || []);
      if(itemsToRemove.length > 0) {
        params.itemsToRemove = itemsToRemove;
      }
      const hostedPage = await ChargebeeService.upgradePlanUsingCheckout(
        params.subscriptionId,
        params.planId,
        params.units,
        params.trialEnd,
        params.couponId,
        undefined,
        undefined,
        undefined,
        undefined,
        itemsToRemove,
      );

      if (hostedPage?.hosted_page?.url) {
        window.location.href = hostedPage.hosted_page.url;
      } else {
        console.error("No hosted page URL received");
      }
    } catch (error) {
      console.error("Error upgrading plan:", error);
      
      // Track plan upgrade/downgrade error
      // if (isPlanUpgrade) {
      //   const currentPlan = subscriptionData?.subscription_items?.find(item => item.itemType === "plan")?.itemPriceId || '';
      //   const currentUnits = subscriptionData?.subscription_items?.find(item => item.itemType === "plan")?.quantity || 1;
      //   const newUnits = units || 1;
      //   const isDowngrade = newUnits < currentUnits || 
      //                      (newUnits === currentUnits && baseItemId !== currentPlan);

      //   const errorData = {
      //     'Current Plan': currentPlan,
      //     'Attempted Plan': itemId,
      //     'Is Downgrade': isDowngrade,
      //     'Error Type': error instanceof Error ? error.message : 'Unknown Error',
      //     'Page Title': document.title,
      //     'Page URL': window.location.href,
      //     'Timestamp': new Date().toISOString(),
      //   };
      //   CleverTapService.trackError('Plan Change Error', error instanceof Error ? error.message : 'Unknown Error', {}, errorData);
      // }
    }
  };

  const handleCancelAddon = async (addonId: string) => {
    try {
      const subscriptionId = subscriptionData?.subscription?.id;

      if (!subscriptionId) {
        console.error("No subscription ID available");
        return;
      }

      console.log("Cancelling addon:", addonId, "for subscription:", subscriptionId);

      // Call ChargebeeService to cancel the addon
      const result = await ChargebeeService.openEditSubscriptionSection(subscriptionId);

    } catch (error) {
      console.error("Error cancelling addon:", error);
      toast.error("An error occurred while cancelling the add-on");
    } finally {
      setCancelAddonModal(false);
    }
  };

  const isLive = liveAttendeesMaxCount !== 0;
  const isAutomated = participantsMaxCount !== 0;
  
  return (
    <Container sx={{ maxWidth: "1500px !important", px: "0!important" }}>
      <div role="presentation" onClick={handleClick}>
        <Breadcrumbs
          aria-label="subsciption details breadcrumb"
          sx={{
            color: "#178EFC",
            fontSize: "14px",
            paddingTop: "0 !important",
            "& li:last-child a": { color: "#000000" },
          }}
        >
          <Link
            underline="hover"
            color="#178EFC"
            href="/"
            onClick={() => {
              navigate("/subscription");
            }}
          >
            Subscriptions
          </Link>
          <Link underline="hover" href="#">
            Easywebinar
          </Link>
        </Breadcrumbs>
      </div>

      {/* updated design for this details section */}
      <Paper
        sx={{
          p: { xs: "16px", sm: "24px" },
          mt: "28px",
          borderRadius: "8px",
          border: "1px solid #E8EAF0",
          boxShadow: "none",
        }}
      >

        <Box
          display={"flex"}
          justifyContent={"space-between"}
          flexWrap={"wrap"}
          gap={"16px"}
        >
          <Box display={"flex"} alignItems={"center"}>
            <Typography
              variant="h2"
              color="initial"
              display={"inline"}
              fontSize={"24px"}
              fontWeight={"500"}
            >
              {subscriptionData?.subscription_items
                .find((item) => item.itemType === "plan")
                ?.itemName}
            </Typography>
            {subscriptionData?.subscription && (
              <Box
                display={"inline-flex"}
                width={"fit-content"}
                p={"3px 12px"}
                borderRadius={"10px"}
                bgcolor={`${getStatusDisplay(subscriptionData.subscription).color}0f`}
                color={getStatusDisplay(subscriptionData.subscription).color}
                ml={3}
                fontSize={"16px"}
                fontWeight={"500"}
              >
                {getStatusDisplay(subscriptionData.subscription).text}
              </Box>
            )}
          </Box>

          {/* Sale banner - show if sale is live AND (subscription is in trial OR plan is free-trial OR billing period is monthly/yearly) */}
          {shouldShowSale && (
            <Box 
              sx={{ 
                width: '100%', 
                mt: 2, 
                mb: 1, 
                display: 'flex', 
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => {
                const pricingSection = document.getElementById("pricing-section");
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              <img
                src={
                  subscriptionData?.subscription?.status === "active" && 
                  subscriptionData?.subscription?.billingPeriodUnit === "month"
                    ? "https://easywebinar-site.easywebinar.com/images/susbcription-details-banner.png"
                    : "https://easywebinar-site.easywebinar.com/images/overview-sale-banner.png"
                }
                alt="Sale Banner"
                fetchPriority="high"
                style={{ width: '100%', maxWidth: 1600, borderRadius: 12, boxShadow: '0px 4px 24px 0px #2B2B3040' }}
              />
            </Box>
          )}

          {/* Monthly to Yearly upgrade banner */}
          {/* {shouldShowSale && (
            <Paper sx={{
              p: "20px 24px",
              mt: "16px",
              borderRadius: "8px",
              border: "2px solid #22B060",
              bgcolor: "#F0F9F4",
              boxShadow: "none",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
              width: "100%",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                bgcolor: "#E8F5E8",
                transform: "translateY(-1px)",
                boxShadow: "0px 4px 12px rgba(34, 176, 96, 0.15)"
              }
            }}
            onClick={() => {
              const pricingSection = document.getElementById("pricing-section");
              if (pricingSection) {
                pricingSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            <Box flex={1} maxWidth={700} mr={3}>
              <Typography variant="subtitle1" color="initial" fontWeight={"500"} mb={1} fontSize={`min(calc(16px + 2 * (100vw - 400px) / (1920 - 400)), 18px)`}>
                🎉 Freedom Sale: Save 50% with Annual Billing!
              </Typography>
              <Typography variant="body1" color={theme.palette.text.secondary} fontSize={`min(calc(14px + 2 * (100vw - 400px) / (1920 - 400)), 16px)`}>
                Switch to annual billing during our sale and enjoy significant savings. Use code <Box component="span" sx={{ bgcolor: "#22B060", color: "white", px: 1, py: 0.5, borderRadius: 1, fontWeight: "bold", fontSize: "inherit" }}>EW50</Box> to get 50% off during checkout.
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{ 
                mt: { xs: "16px", sm: "0" }, 
                borderRadius: "8px",
                bgcolor: "#22B060",
                "&:hover": {
                  bgcolor: "#1a8a4d"
                }
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering the parent onClick
                const pricingSection = document.getElementById("pricing-section");
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
              size="large"
            >
              Switch to Annual
            </Button>
          </Paper>
          )} */}

        </Box>
        <Grid container spacing={{ xs: 2.25 }} mt="18px">
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4 }}>
            <Box
              p="24px 31px"
              borderRadius="8px"
              border="2px solid #E8EAF0"
              height={"100%"}
            >
              <Typography variant="body1" color={theme.palette.text.secondary} mb="16px">Next Billing Date</Typography>
              <Typography
                variant="subtitle1"
                display={"flex"}
                alignItems={"center"}
                gap="12px"
                fontWeight={"500"}
                fontSize={`min(calc(16px + 0.5vw), 20px)`}
              >
                {isFreeTrialPlan ? (
                  "-- --"
                ) : (
                  <>
                    <CalenderIcon />
                    {formatDate(subscriptionData?.subscription?.nextBillingAt)}
                  </>
                )}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 4, xl: 4 }} order={{ xs: 2, lg: 0 }}>
            <Box
              p="24px 31px"
              borderRadius="8px"
              border="2px solid #E8EAF0"
              height={"100%"}
            >
              <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb="16px" flexWrap={"wrap"} gap={"8px"}>
                <Typography variant="body1" color={theme.palette.text.secondary} >Billing Cycle</Typography>

                {/* ------------------------IMPLEMENT IN FUTURE ------------------------ */}
                {/* <FormGroup>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography color="#22B060" fontWeight="500" variant="body2">
                      Monthly
                    </Typography>
                    <AntSwitch
                      checked={isAnnual}
                      onChange={handlePlanToggle}
                      inputProps={{ "aria-label": "ant design" }}
                    />
                    <Typography display="flex" alignItems="center" gap={1} variant="body2">
                      Annual
                      <Box
                        sx={{
                          bgcolor: "#E7F9EF",
                          color: "#22B060",
                          p: "4px 10px",
                          borderRadius: "100px",
                          fontSize: "12px",
                          display: "inline-flex",
                          alignItems: "center"
                        }}
                      >
                        Save 17%
                      </Box>
                    </Typography>
                  </Stack>
                </FormGroup> */}
                {/* ------------------------IMPLEMENT IN FUTURE ------------------------ */}

              </Box>
              <Typography
                variant="subtitle1"
                display={"flex"}
                alignItems={"center"}
                gap="12px"
                fontWeight={"500"}
                fontSize={`min(calc(16px + 0.5vw), 20px)`}
              >
                {isFreeTrialPlan ? (
                  "-- --"
                ) : (
                  <>
                    <TimerIconMd />
                    {subscriptionData?.subscription?.billingPeriodUnit === "year" ? "Yearly" : "Monthly"}
                  </>
                )}
              </Typography>
            </Box></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4 }}><Box
            p="24px 31px"
            borderRadius="8px"
            border="2px solid #E8EAF0"
            height={"100%"}
          >
            <Typography variant="body1" color={theme.palette.text.secondary} mb="16px">Pricing</Typography>
            <Typography
              variant="subtitle1"
              display={"flex"}
              alignItems={"center"}
              gap="12px"
              fontWeight={"500"}
              fontSize={`min(calc(16px + 0.5vw), 20px)`}
            >
              $ {calculateNextBillingAmount()}
            </Typography>
          </Box> </Grid>

          <Grid size={{ xs: 12, md: 8, lg: 8 }} order={{ xs: 3, lg: 0 }}>
            <Box
              p="24px 31px"
              borderRadius="8px"
              border="2px solid #E8EAF0"
              height={"100%"}
            >
              <Typography variant="h6" color={theme.palette.text.primary} mb="16px">Included Features</Typography>
              <SubscriptionFeatures
                planType={
                  isFreeTrialPlan ? 'trial' :
                    isAutomatedPlan ? 'automated' :
                      isLivePlan ? 'live' :
                        isLiveAutomatedPlan ? 'all-in-one' : 'trial'
                }
                quantity={ participantsMaxCount + liveAttendeesMaxCount }
                storageMax={storageUsageMaxCount}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} order={{ xs: 4, lg: 0 }}><Box
            p="24px 31px"
            borderRadius="8px"
            border="2px solid #E8EAF0"
            height={"100%"}
          >
            <Box
              className="storage-box-con"
              p={{ sx: "0 0 20px", xl: "0 20px 20px" }}
              display={"flex"}
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
            >
              <Typography
                variant="h5"
                color="#000000"
                fontWeight={"500"}
                textAlign="start"
                width="100%"
              >
                Storage
              </Typography>
              <Box
                className="storage-box"
                sx={{ position: "relative" }}
                height={"100%"}
                maxHeight="142px"
                width={"100%"}
                maxWidth={"142px"}
                flexDirection="column"
                alignItems="center"
              >
                {usageLoading ? (
                  <>
                    <Skeleton variant="circular" width={142} height={142} />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Skeleton variant="text" width={80} height={24} />
                    </Box>
                  </>
                ) : (
                  <>
                    <CircularProgress
                      variant="determinate"
                      value={storageUsageMaxCount > 0 ? (Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? 100 : Math.ceil((storageUsageValue / storageUsageMaxCount) * 100)) : 0}
                      size={"100%"}
                      color="primary"
                      sx={(theme) => ({
                        color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#22B060",
                        animationDuration: "550ms",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        zIndex: 1,
                        [`& .${circularProgressClasses.circle}`]: {
                          strokeLinecap: "round",
                          color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#22B060",
                        },
                        ...theme.applyStyles("dark", {
                          color: "#308fe8",
                        }),
                      })}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={"100%"}
                      color="primary"
                      sx={(theme) => ({
                        color: "#E8E8EA",
                        animationDuration: "550ms",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        [`& .${circularProgressClasses.circle}`]: {
                          strokeLinecap: "round",
                          color: "#E8E8EA",
                        },
                        ...theme.applyStyles("dark", {
                          color: "#E8E8EA",
                        }),
                      })}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          color: storageUsageMaxCount > 0 && Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) > 100 ? "#E44258" : "#333333",
                          fontSize: "18px",
                          fontWeight: "500",
                        }}
                      >
                        {storageUsageMaxCount > 0 ? Math.ceil(
                          (storageUsageValue / storageUsageMaxCount) * 100
                        ).toString() : "0"}
                        % Used
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
              <Box mt="16px">
                <Box display={"flex"} alignItems="center" mb={2}>
                  <Box
                    className="small-cirle"
                    bgcolor={"#E8E8EA"}
                    borderRadius={"100%"}
                    height={"15px"}
                    width={"15px"}
                    mr={2}
                  ></Box>
                  <Typography
                    variant="subtitle1"
                    color="#000000"
                    fontSize={{ sm: "14px", md: "16px" }}
                  >
                    {usageLoading ? (
                      <Skeleton variant="text" width={120} height={20} />
                    ) : (
                      <>
                        {storageUsageMaxCount} {storageUsageUnit} Total
                      </>
                    )}
                  </Typography>
                </Box>
                <Box display={"flex"} alignItems="center">
                  <Box
                    className="small-cirle"
                    bgcolor={"#22B060"}
                    borderRadius={"100%"}
                    height={"15px"}
                    width={"15px"}
                    mr={2}
                  ></Box>
                  <Typography variant="subtitle1" color="#000000">
                    {usageLoading ? (
                      <Skeleton variant="text" width={100} height={20} />
                    ) : (
                      <>
                        {storageUsageValue.toFixed(2)} {storageUsageUnit} Used
                      </>
                    )}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="center" mt="24px">
                  <Button variant="contained" color="primary" size="large" sx={{ borderRadius: "8px" }} onClick={
                    () => {
                      MixpanelService.track('ua_subscription_buy_storage_clicked', { 'Click "Buy More" (storage widget)': 'storage_used_gb: ' + storageUsageValue + ', storage_total_gb: ' + storageUsageMaxCount + ', pct_used: ' + Math.ceil((storageUsageValue / storageUsageMaxCount) * 100) });
                      scrollToAddOns();
                    }}>
                    Buy More
                  </Button>
                </Box>

              </Box>
            </Box>
          </Box> </Grid>

          <Grid size={12} order={{ xs: 5, lg: 0 }}>
            <Box
              p="24px 31px 32px"
              borderRadius="8px"
              border="2px solid #E8EAF0"
              height={"100%"}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb="16px" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h6" color={theme.palette.text.primary} mb="4px">Monthly Usage</Typography>
                  <Typography variant="body2" color="#666666" fontSize="12px">
                    Limits reset at the end of the billing cycle
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {/* Left Arrow */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      const maxMonths = usageCycles.length > 0 ? Math.max(...usageCycles.map(c => c.months)) : 0;
                      if (selectedMonths < maxMonths) {
                        const next = selectedMonths + 1;
                        setSelectedMonths(next);
                        trackUsagePeriodChanged("arrows", next);
                      }
                    }}
                    disabled={
                      (usageCycles.length > 0 && selectedMonths >= Math.max(...usageCycles.map(c => c.months))) || 
                      subscriptionData?.subscription?.status === 'in_trial' ||
                      usageCycles.length <= 1
                    }
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "4px",
                      border: "1px solid #E8EAF0",
                      bgcolor: "white",
                      "&:hover": { bgcolor: "#F5F5F5" },
                      "&.Mui-disabled": { opacity: 0.5 }
                    }}
                  >
                    <ChevronLeft sx={{ fontSize: "20px", color: "#666666" }} />
                  </IconButton>

                  {/* Date Range Display with Dropdown */}
                  <Box
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: "8px",
                      border: "1px solid #178EFC",
                      bgcolor: "#F0F9FC",
                      cursor: "pointer",
                      minWidth: "280px",
                      "&:hover": {
                        bgcolor: "#E8F5F9"
                      }
                    }}
                  >
                    <CalenderIcon />
                    {usageDateRange ? (
                      <Typography variant="body2" sx={{ color: "#178EFC", fontSize: "14px", fontWeight: 500 }}>
                        {usageDateRange.split(" (")[0]}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#178EFC", fontSize: "14px" }}>
                        Loading...
                      </Typography>
                    )}
                    {usageCycles.find(c => c.months === selectedMonths)?.isCurrent && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "4px",
                          bgcolor: "#178EFC",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: 500
                        }}
                      >
                        Current
                      </Box>
                    )}
                    <KeyboardArrowDown sx={{ fontSize: "20px", color: "#666666", ml: "auto" }} />
                  </Box>

                  {/* Dropdown Menu */}
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 320,
                        borderRadius: "8px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                        maxHeight: 400,
                        overflow: "auto"
                      }
                    }}
                  >
                    {/* <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E8EAF0" }}>
                      <Typography variant="caption" sx={{ color: "#666666", fontSize: "12px", fontWeight: 500, textTransform: "uppercase" }}>
                        USAGE CYCLES
                      </Typography>
                    </Box> */}
                    {usageCycles.map((cycle) => (
                      <MenuItem
                        key={cycle.months}
                        onClick={() => {
                          // Only update and show loading if selecting a different cycle
                          if (selectedMonths !== cycle.months) {
                            setSelectedMonths(cycle.months);
                            setUsageLoading(true); // Show skeleton when selection changes
                            trackUsagePeriodChanged("dropdown", cycle.months);
                          }
                          setAnchorEl(null);
                        }}
                        selected={selectedMonths === cycle.months}
                        sx={{
                          px: 2,
                          py: 1.5,
                          bgcolor: selectedMonths === cycle.months ? "#F5F5F5" : "transparent",
                          "&:hover": {
                            bgcolor: "#F5F5F5"
                          },
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5
                        }}
                      >
                        <CalenderIcon />
                        <Typography variant="body2" sx={{ color: "#333333", fontSize: "14px", flex: 1 }}>
                          {cycle.dateRange}
                        </Typography>
                        <Box display="flex" gap={1} alignItems="center">
                          {/* {cycle.hasOverage && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "4px",
                                bgcolor: "#E44258",
                                color: "white",
                                fontSize: "11px",
                                fontWeight: 500
                              }}
                            >
                              Overage
                            </Box>
                          )} */}
                          {cycle.isCurrent && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "4px",
                                bgcolor: "#178EFC",
                                color: "white",
                                fontSize: "11px",
                                fontWeight: 500
                              }}
                            >
                              Current
                            </Box>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Menu>

                  {/* Right Arrow */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (selectedMonths > 0) {
                        const next = selectedMonths - 1;
                        setSelectedMonths(next);
                        trackUsagePeriodChanged("arrows", next);
                      }
                    }}
                    disabled={
                      selectedMonths === 0 || 
                      subscriptionData?.subscription?.status === 'in_trial' ||
                      usageCycles.length <= 1
                    }
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "4px",
                      border: "1px solid #E8EAF0",
                      bgcolor: "white",
                      "&:hover": { bgcolor: "#F5F5F5" },
                      "&.Mui-disabled": { opacity: 0.5 }
                    }}
                  >
                    <ChevronRight sx={{ fontSize: "20px", color: "#666666" }} />
                  </IconButton>
                </Box>
              </Box>
              {/* live attendees count */}
              {(isLive) && (() => {
                const liveUsagePercentage = liveAttendeesMaxCount > 0 ? Math.ceil((liveParticipantsUsageCount / liveAttendeesMaxCount) * 100) : 0;
                const usageColor = getUsageColor(liveUsagePercentage);
                const hasOverage = liveUsagePercentage > 100;
                const overageCount = hasOverage ? liveParticipantsUsageCount - liveAttendeesMaxCount : 0;
                
                // Calculate end date of selected usage cycle
                const getCycleEndDate = () => {
                  const selectedCycle = usageCycles.find(cycle => cycle.months === selectedMonths);
                  if (selectedCycle?.termEnd) {
                    const endDate = new Date(selectedCycle.termEnd * 1000);
                    return endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  }
                  // Fallback to current term end if cycle not found
                  if (!subscriptionData?.subscription?.currentTermEnd) return '';
                  const endDate = new Date(subscriptionData.subscription.currentTermEnd * 1000);
                  return endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                };
                
                return (
                  <Box 
                    width={"100%"}
                    p="16px"
                    borderRadius="8px"
                    border={`1px solid ${usageColor === "#178EFC" ? "#E8EAF0" : usageColor === "#FFA500" ? "#FFA500" : "#FFFBFB"}`}
                    bgcolor={usageColor === "#178EFC" ? "transparent" : usageColor === "#FFA500" ? "#FFF9E6" : "#FEE8E8"}
                  >
                    <Typography
                      color="#333333"
                      variant="body2"
                      mb="6px"
                      textAlign="left"
                      display="flex"
                      alignItems="center"
                    >
                      Live Attendees
                      <Tooltip arrow title="Overage Charges: $0.78 per lead above your plan limit">
                        <Box component="span" sx={{ display: 'inline-flex', ml: 1, color: usageColor }}>
                          <InfoBlueIcon />
                        </Box>
                      </Tooltip>
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="6px">
                      <Box></Box>
                      {usageLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Skeleton variant="text" width={80} height={20} />
                          <Skeleton variant="rounded" width={60} height={20} />
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            color={usageColor}
                            fontSize="12px"
                            textAlign="right"
                            fontWeight={500}
                          >
                            {liveParticipantsUsageCount} of {liveAttendeesMaxCount}
                          </Typography>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: "4px",
                              bgcolor: usageColor === "#178EFC" ? "#E8F5F9" : usageColor === "#FFA500" ? "#FFF9E6" : "#FEE8E8",
                              border: `1px solid ${liveUsagePercentage > 100 ? "#E44258" : usageColor}`,
                              color: usageColor,
                              fontSize: "11px",
                              fontWeight: 500,
                              marginBottom: "4px",
                            }}
                          >
                            {liveUsagePercentage}% used
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Box position="relative" width="100%">
                      {usageLoading ? (
                        <Skeleton variant="rounded" width="100%" height={8} sx={{ borderRadius: "18px" }} />
                      ) : (
                        <BorderLinearProgressGreen
                          variant="determinate"
                          value={liveAttendeesMaxCount > 0 ? Math.min(liveUsagePercentage, 100) : 0}
                          usagePercentage={liveUsagePercentage}
                          sx={{
                            display: liveAttendeesMaxCount > 0 ? 'block' : 'none',
                            minHeight: '8px',
                            width: '100%',
                          }}
                        />
                      )}
                      {hasOverage && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            bgcolor: "#E44258",
                            color: "#FFFFFF",
                            fontSize: "11px",
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          +{overageCount} over
                        </Box>
                      )}
                    </Box>
                    {/* Detailed breakdown - only show when there's overage */}
                    {/* {hasOverage && (
                      <Box mt="16px" display="flex" flexDirection="column" gap="8px">
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Included:</Typography>
                          <Typography fontSize="12px" color="#333333" fontWeight={500}>{liveAttendeesMaxCount}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Overage:</Typography>
                          <Typography fontSize="12px" color="#E44258" fontWeight={500}>{overageCount}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Total used:</Typography>
                          <Typography fontSize="12px" color="#333333" fontWeight={500}>{liveParticipantsUsageCount}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Est. Overage Cost:</Typography>
                          <Typography fontSize="12px" color="#E44258" fontWeight={500}>
                            ${(overageCount * 0.78).toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography fontSize="11px" color="#999999" mt="4px">
                          @$0.78/lead
                        </Typography>
                      </Box>
                    )} */}
                    {liveUsagePercentage >= 61 && (
                      <Box 
                        mt="12px" 
                        p="12px" 
                        borderRadius="4px" 
                        bgcolor="#FFF9E6"
                        border={`1px solid #E8F5F9`}
                      >
                        <Box display="flex" alignItems="flex-start" gap={1} bgcolor="#FFF9E6">
                          <Box component="span" sx={{ color: liveUsagePercentage > 100 ? "#E44258" : "#FFA500", fontSize: "18px", display: "inline-flex", alignItems: "center", mt: "2px" }}>
                            <AlertIcon />
                          </Box>
                          <Box>
                            <Typography color={liveUsagePercentage > 100 ? "#E44258" : "#FFA500"} fontSize="12px" lineHeight="1.5">
                              {liveUsagePercentage > 100 
                                ? `You've reached your limit. ` 
                                : `You're approaching your limit. `
                              }
                              <Typography 
                                component="span" 
                                onClick={scrollToUpgradePlan} 
                                fontSize="inherit" 
                                color="#178EFC" 
                                sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                              >
                                Upgrade your plan
                              </Typography>
                              {liveUsagePercentage > 100 ? ' or overages will apply.' : ' to avoid overcharges.'}
                            </Typography>
                            {liveUsagePercentage > 100 && (
                              <Typography fontSize="12px" mt="4px" color="666666">
                                Overages will be calculated at the end of this usage cycle on {getCycleEndDate()}.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}
              {/* automated attendees count */}
              {(isAutomated) && (() => {
                const automatedUsagePercentage = participantsMaxCount > 0 ? Math.ceil((automatedParticipantsUsageCount / participantsMaxCount) * 100) : 0;
                const usageColor = getUsageColor(automatedUsagePercentage);
                const hasOverage = automatedUsagePercentage > 100;
                const overageCount = hasOverage ? automatedParticipantsUsageCount - participantsMaxCount : 0;
                
                // Calculate end date of selected usage cycle
                const getCycleEndDate = () => {
                  const selectedCycle = usageCycles.find(cycle => cycle.months === selectedMonths);
                  if (selectedCycle?.termEnd) {
                    const endDate = new Date(selectedCycle.termEnd * 1000);
                    return endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  }
                  // Fallback to current term end if cycle not found
                  if (!subscriptionData?.subscription?.currentTermEnd) return '';
                  const endDate = new Date(subscriptionData.subscription.currentTermEnd * 1000);
                  return endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                };
                
                return (
                  <Box
                    width={"100%"} 
                    mt="40px"
                    p="16px"
                    borderRadius="8px"
                    border={`1px solid ${usageColor === "#178EFC" ? "#E8EAF0" : usageColor === "#FFA500" ? "#FFA500" : "#FFFBFB"}`}
                    bgcolor={usageColor === "#178EFC" ? "transparent" : usageColor === "#FFA500" ? "#FFF9E6" : "#FEE8E8"}
                  >
                      <Typography
                        color={"#333333"}
                        variant="body2"
                        mb="6px"
                        textAlign="left"
                        display="flex"
                        alignItems="center"
                      >
                        Automated Attendees
                        <Tooltip arrow title="Overage Charges: $0.078 per lead above your plan limit">
                          <Box component="span" sx={{ display: 'inline-flex', ml: 1, color: usageColor }}>
                            <InfoBlueIcon />
                          </Box>
                        </Tooltip>
                      </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="6px">
                      <Box></Box>
                      {usageLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Skeleton variant="text" width={80} height={20} />
                          <Skeleton variant="rounded" width={60} height={20} />
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            color={hasOverage ? "#E44258" : usageColor}
                            fontSize="12px"
                            textAlign="right"
                            fontWeight={500}
                          >
                            {automatedParticipantsUsageCount} of {participantsMaxCount}
                          </Typography>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: "4px",
                              bgcolor: usageColor === "#178EFC" ? "#E8F5F9" : usageColor === "#FFA500" ? "#FFF9E6" : "#FEE8E8",
                              border: `1px solid ${hasOverage ? "#E44258" : usageColor}`,
                              color: hasOverage ? "#E44258" : usageColor,
                              fontSize: "11px",
                              fontWeight: 500
                            }}
                          >
                            {automatedUsagePercentage}% used
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Box position="relative" width="100%">
                      {usageLoading ? (
                        <Skeleton variant="rounded" width="100%" height={8} sx={{ borderRadius: "18px" }} />
                      ) : (
                        <BorderLinearProgressGreen
                          variant="determinate"
                          value={(isAutomated) ? (participantsMaxCount > 0 ? Math.min(automatedUsagePercentage, 100) : 0) : 0}
                          usagePercentage={automatedUsagePercentage}
                          sx={{
                            opacity: (isAutomated) ? 1 : 0.5,
                            display: participantsMaxCount > 0 ? 'block' : 'none',
                            minHeight: '8px',
                            width: '100%',
                          }}
                        />
                      )}
                      {hasOverage && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            bgcolor: "#E44258",
                            color: "#FFFFFF",
                            fontSize: "11px",
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          +{overageCount} over
                        </Box>
                      )}
                    </Box>
                    {/* Detailed breakdown - only show when there's overage */}
                    {/* {hasOverage && (
                      <Box mt="16px" display="flex" flexDirection="column" gap="8px">
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Included:</Typography>
                          <Typography fontSize="12px" color="#333333" fontWeight={500}>{participantsMaxCount}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Overage:</Typography>
                          <Typography fontSize="12px" color="#E44258" fontWeight={500}>{overageCount}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Total used:</Typography>
                          <Typography fontSize="12px" color="#333333" fontWeight={500}>{automatedParticipantsUsageCount}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontSize="12px" color="#666666">Est. Overage Cost:</Typography>
                          <Typography fontSize="12px" color="#E44258" fontWeight={500}>
                            ${(overageCount * 0.078).toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography fontSize="11px" color="#999999" mt="4px">
                          @$0.078/lead
                        </Typography>
                      </Box>
                    )} */}
                    {automatedUsagePercentage >= 61 && (
                      <Box 
                        mt="12px" 
                        p="12px" 
                        borderRadius="4px" 
                        bgcolor="#FFF9E6"
                        border={`1px solid #E8F5F9`}
                      >
                        <Box display="flex" alignItems="flex-start" gap={1} bgcolor="#FFF9E6">
                          <Box component="span" sx={{ color: automatedUsagePercentage > 100 ? "#E44258" : "#FFA500", fontSize: "18px", display: "inline-flex", alignItems: "center", mt: "2px" }}>
                            <AlertIcon />
                          </Box>
                          <Box>
                            <Typography color={automatedUsagePercentage > 100 ? "#E44258" : "#FFA500"} fontSize="12px" lineHeight="1.5">
                              {automatedUsagePercentage > 100 
                                ? `You've reached your limit. ` 
                                : `You're approaching your limit. `
                              }
                              <Typography 
                                component="span" 
                                onClick={scrollToUpgradePlan} 
                                fontSize="inherit" 
                                color="#178EFC" 
                                sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                              >
                                Upgrade your plan
                              </Typography>
                              {automatedUsagePercentage > 100 ? ' or overages will apply.' : ' to avoid overcharges.'}
                            </Typography>
                            {automatedUsagePercentage > 100 && (
                              <Typography fontSize="12px" mt="4px" color="666666">
                                Overages will be calculated at the end of this usage cycle on {getCycleEndDate()}.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {/* updated design for this details section */}

      {/* pricing table v2 */}
      {/* <Box id="pricing-section">
        <PricingSection
          currentPlanId={subscriptionData?.subscription_items.find(
            (item) => item.itemType === "plan"
          )?.itemPriceId}
          currentAttendees={subscriptionData?.subscription_items.find(
            (item) => item.itemType === "plan"
          )?.quantity}
          onPlanSelect={(planId) => {
            handleUpgradePlan(planId);
          }}
        />
      </Box> */}

      <PricingSectionV2Sale
        id="pricing-section"
        currentPlanId={subscriptionData?.subscription_items.find(
          (item) => item.itemType === "plan"
        )?.itemPriceId}
        currentAttendees={subscriptionData?.subscription_items.find(
          (item) => item.itemType === "plan"
        )?.quantity || 0}
        onPlanSelect={(planId) => {
          handleUpgradePlan(planId, true);
        }}
        // isSaleLive={shouldShowSale}
        isSaleLive={false}
        subscriptionStatus={subscriptionData?.subscription?.status}
      />
      {/* <BlackFridayBanner />

      <BlackFridayPricingSection 
        id="pricing-section"
        subscriptionId={subscriptionData?.subscription?.id || ''} 
        currentPlanId={subscriptionData?.subscription_items.find(
            (item) => item.itemType === "plan"
          )?.itemPriceId || ''} 
        amount={(subscriptionData?.subscription_items.find(
          (item) => item.itemType === "plan"
        )?.unitPrice || 0) / 100} 
        />

      <BlackFridayBonuses /> */}
      {/* ==================pricing table ================= */}

      {/* ===================subscribed add ons ================ */}
      {subscriptionData?.subscription_items.some(item => item.itemType === "addon") && (
        <Paper sx={{
          p: "24px",
          mt: "39px",
          borderRadius: "8px",
          border: "1px solid #E8EAF0",
          boxShadow: "none",
        }}>
          <Typography variant="h5" color="initial" fontWeight={"500"} mb={3}>
            Subscribed Add-Ons
          </Typography>
          <Grid
            container
            rowSpacing={{ xs: 3, sm: 3, md: 3 }}
            columnSpacing={{ xs: 3, sm: 2, md: 3 }}
          >
            {/* Subscribed Add-ons */}
            {subscriptionData?.subscription_items
              .filter(item => item.itemType === "addon")
              .map((addon) => (
                <Grid key={addon.itemPriceId} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <Card sx={{
                    padding: "26px 23px",
                    border: "1px solid #E8EAF0",
                    boxShadow: "0px 4px 12px 0px rgba(43, 43, 48, 0.08)",
                    display: "flex",
                    flexDirection: "column !important",
                    justifyContent: "space-between",
                    position: "relative",
                    height: "100%",
                    borderRadius: "8px",
                    minHeight: "300px",
                  }}>
                    <Box>
                      <Box mb="24px" display="flex" gap="18px" alignItems="center">
                        {addon.itemPriceId.includes('White-Label-Branding') && <img src={file} />}
                        {addon.itemPriceId.includes('Storage-Add-on-2-GB') && <StorageIcon />}
                        {addon.itemPriceId.includes('One-Time-Webinar-Assist-and-Setup') && <SupportAgentIcon />}
                        {addon.itemPriceId.includes('custom-domain') && <img src={file} />}
                        <Typography fontSize="18px" fontWeight="500" color="#333333">
                          {getStaticAddonByItemPriceId(addon.itemPriceId)?.title ?? addon.itemName}
                        </Typography>
                      </Box>
                      <Typography color="#333333" fontSize="14px" lineHeight="24px" mb="24px">
                        {(() => {
                          const staticAddon = getStaticAddonByItemPriceId(addon.itemPriceId);
                          if (staticAddon) return staticAddon.description;
                          if (addon.itemPriceId.includes('White-Label-Branding')) return "Remove EasyWebinar branding and customize the platform with your own brand identity for a seamless, professional appearance.";
                          if (addon.itemPriceId.includes('Storage-Add-on-2-GB')) return "Expand your webinar storage capacity with additional space for recordings, presentations, and resources to ensure seamless content delivery.";
                          if (addon.itemPriceId.includes('One-Time-Webinar-Assist-and-Setup')) return "Get expert assistance in setting up your webinar from start to finish. Our team will help configure your settings, customize templates, and ensure everything runs smoothly.";
                          if (addon.itemPriceId.includes('custom-domain')) return "Personalize your webinar experience with a custom domain that reflects your brand's identity.";
                          if (addon.itemPriceId.includes('EasyCRM')) return "Complete CRM & automation";
                          if (addon.itemPriceId.includes('Paid-Webinar')) return "Monetize your webinars";
                          return "";
                        })()}
                      </Typography>
                    </Box>
                    <Box>
                      <Box display="flex" alignItems="start" justifyContent="space-between">
                        <Typography color="#666666" fontSize="14px">
                          Renews on {formatDate(subscriptionData.subscription.nextBillingAt)}
                        </Typography>
                        <Typography color="#22B060" fontSize="20px" fontWeight="600" display="flex" alignItems="end">
                          {addon.unitPrice === 0 ? (
                            "Free"
                          ) : (
                            <>
                              ${(addon.unitPrice / 100).toFixed(2)}
                              <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                                / month
                              </Typography>
                            </>
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="end" mt="12px">
                        <Button
                          variant="outlined"
                          color="error"
                          sx={{
                            height: "32px",
                            fontSize: "14px",
                            px: "12px",
                            borderRadius: "4px",
                            textTransform: "capitalize",
                          }}
                          onClick={() => {
                            setSelectedAddonToCancel(addon.itemPriceId);
                            setCancelAddonModal(true);
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}

            {/* Add More Card */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
              <Card
                sx={{
                  padding: "26px 23px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "0px 4px 12px 0px rgba(43, 43, 48, 0.08)",
                  display: "flex",
                  flexDirection: "column !important",
                  justifyContent: "center",
                  position: "relative",
                  height: "100%",
                  borderRadius: "8px",
                  minHeight: "300px",
                }}
              >
                <Box display="flex" flexDirection="column">
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <Box
                      display="flex"
                      sx={{ "& img": { maxWidth: "100%", height: "auto" } }}
                    >
                      <img
                        src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/Illustration.webp"
                        alt="illustration-img"
                      />
                    </Box>
                    <Typography color="#111824" fontSize="14px" my="24px">
                      Explore more add-on features
                    </Typography>
                    <Box textAlign="center">
                      <Button
                        sx={{
                          textTransform: "capitalize",
                          bgcolor: "#178EFC",
                          color: "#FFFFFF !important",
                          px: "24px",
                          height: "40px",
                          fontSize: "16px",
                          cursor: "pointer",
                          borderRadius: "8px",
                          "&:hover": {
                            border: "none !important",
                            bgcolor: "#178EFC",
                          },
                        }}
                        onClick={scrollToAddOns}
                      >
                        Add More
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}
      {/* ===================available add ons ================ */}
      <Box mt="42px" id="available-add-ons">
        <Typography variant="h5" color="#333333" fontWeight="500" mb="24px">
          Available Add-Ons
        </Typography>
        {isFreeTrialPlan && (
          <Paper sx={{
            p: "16px 24px",
            mb: "24px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.primary.main}`,
            bgcolor: "#F2F9FC",
            boxShadow: "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}>
            <Box flex={1} maxWidth={700} mr={3}>
              <Typography variant="body1" color={theme.palette.text.secondary} fontSize="16px">
                Add-ons are only available in the paid plans.
              </Typography>
            </Box>
          </Paper>
        )}
        <Box display="flex" gap="20px" flexWrap="wrap">
          {/* Filter out subscribed add-ons */}
          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId.includes("White-Label-Branding")) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <img src={file} />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        White Label
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Remove EasyWebinar branding and customize the platform with
                      your own brand identity for a seamless, professional
                      appearance.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $99
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton
                      isTrialMode={isFreeTrialPlan}
                      isPausedPlan={isPausedPlan}
                      addonName="White Label"
                      addonPrice={99}
                      onClick={() => handleUpgradePlan("White-Label-Branding", false)}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId.includes("Storage-Add-on-2-GB")) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <StorageIcon />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        Additional Storage (5GB)
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Expand your webinar storage capacity with additional space for
                      recordings, presentations, and resources to ensure seamless
                      content delivery.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $11
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton
                      isTrialMode={isFreeTrialPlan}
                      isPausedPlan={isPausedPlan}
                      addonName="Additional Storage (5GB)"
                      addonPrice={11}
                      onClick={() => handleUpgradePlan("Storage-Add-on-2-GB", false)}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId.includes("One-Time-Webinar-Assist-and-Setup")) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <SupportAgentIcon />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        One Time Webinar Assist & Setup
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Get expert assistance in setting up your webinar from start to
                      finish. Our team will help configure your settings, customize
                      templates, and ensure everything runs smoothly.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $250
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton
                      isTrialMode={isFreeTrialPlan}
                      isPausedPlan={isPausedPlan}
                      addonName="One Time Webinar Assist & Setup"
                      addonPrice={250}
                      onClick={() => handleUpgradePlan("One-Time-Webinar-Assist-and-Setup", false)}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {!subscriptionData?.subscription_items.some(item =>
            item.itemPriceId === "custom-domain") && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": {
                    width: "47%",
                  },
                  "@media (min-width:320px) and (max-width:600px)": {
                    maxWidth: "100%",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection="column"
                  height="100%"
                >
                  <Box mb="24px">
                    <Box mb="24px" display="flex" gap="18px" alignItems="center">
                      <img src={file} />
                      <Typography fontSize="18px" fontWeight="500" color="#333333">
                        C-Name
                      </Typography>
                    </Box>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Personalize your webinar experience with a custom domain that
                      reflects your brand's identity.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap="16px"
                  >
                    <Typography
                      display="flex"
                      alignItems="end"
                      fontSize="20px"
                      color="#22B060"
                      fontWeight="600"
                    >
                      $49
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">
                        / month
                      </Typography>
                    </Typography>
                    <AddonButton
                      isTrialMode={isFreeTrialPlan}
                      isPausedPlan={isPausedPlan}
                      addonName="C-Name"
                      addonPrice={49}
                      onClick={() => handleUpgradePlan("custom-domain", false)}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {addonsAvailableForPlan.easycrm && !subscriptionData?.subscription_items.some(item =>
            item.itemType === "addon" && (item.itemPriceId?.includes("EasyCRM-USD-Monthly") || item.itemPriceId?.includes("EasyCRM-USD-Yearly"))) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": { width: "47%" },
                  "@media (min-width:320px) and (max-width:600px)": { maxWidth: "100%" },
                }}
              >
                <Box display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                  <Box mb="24px">
                    <Typography fontSize="18px" fontWeight="500" color="#333333" mb="24px">
                      EasyCRM
                    </Typography>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Complete CRM & automation
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap="16px">
                    <Typography display="flex" alignItems="end" fontSize="20px" color="#22B060" fontWeight="600">
                      $49
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">/ month</Typography>
                    </Typography>
                    <AddonButton
                      isTrialMode={isFreeTrialPlan}
                      isPausedPlan={isPausedPlan}
                      addonName="EasyCRM"
                      addonPrice={49}
                      onClick={() => handleUpgradePlan(isYearlyPlan? "EasyCRM-USD-Yearly" : "EasyCRM-USD-Monthly", false)}
                    />
                  </Box>
                </Box>
              </Paper>
            )}

          {addonsAvailableForPlan.paidwebinars && !subscriptionData?.subscription_items.some(item =>
            item.itemType === "addon" && (item.itemPriceId?.includes("Paid-Webinar-USD-Monthly") || item.itemPriceId?.includes("Paid-Webinar-USD-Yearly"))) && (
              <Paper
                sx={{
                  maxWidth: "calc(100% / 3 - 14px)",
                  minWidth: "366px",
                  width: "100%",
                  minHeight: "280px",
                  p: "24px 15px",
                  borderRadius: "8px",
                  border: "1px solid #E8EAF0",
                  boxShadow: "none",
                  "@media (min-width:768px) and (max-width:1366px)": { width: "47%" },
                  "@media (min-width:320px) and (max-width:600px)": { maxWidth: "100%" },
                }}
              >
                <Box display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                  <Box mb="24px">
                    <Typography fontSize="18px" fontWeight="500" color="#333333" mb="24px">
                      Paid Webinars
                    </Typography>
                    <Typography color="#333333" fontSize="14px" lineHeight="24px">
                      Monetize your webinars
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap="16px">
                    <Typography display="flex" alignItems="end" fontSize="20px" color="#22B060" fontWeight="600">
                      $49
                      <Typography color="#333333" fontSize="14px" ml="4px" mb="2px">/ month</Typography>
                    </Typography>
                    <AddonButton
                      isTrialMode={isFreeTrialPlan}
                      isPausedPlan={isPausedPlan}
                      addonName="Paid Webinars"
                      addonPrice={49}
                      onClick={() => handleUpgradePlan(isYearlyPlan? "Paid-Webinar-USD-Yearly" : "Paid-Webinar-USD-Monthly", false)}
                    />
                  </Box>
                </Box>
              </Paper>
            )}
        </Box>
      </Box>
      {/* ===================footer-section ================ */}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={{ xs: "10px", sm: "12px" }}
        mt={{ xs: "16px", sm: "24px" }}
      >
        <Typography color="#666666" fontSize="14px">
          Got a question? Get in touch with our
          <a
            href="#"
            onClick={
              (event) => {
                MixpanelService.track("ua_subscription_support_clicked", {
                  subscription_id: subscriptionData?.subscription?.id ?? "",
                  screen: "plans",
                });
                handleSupportTeamClick(event);
              }}
            style={{
              color: "#178EFC",
              marginLeft: "4px",
              textDecoration: "none",
            }}
          >
            Support Team
          </a>
        </Typography>
        {subscriptionData?.subscription?.status !== "cancelled" &&
          subscriptionData?.subscription?.status !== "non_renewing" && (
            <Button
              variant="outlined"
              color="error"
              sx={{
                textTransform: "capitalize",
                height: "32px",
                fontSize: "14px",
                px: "12px",
                borderRadius: "4px",
              }}
              onClick={() => {
                const planItem = subscriptionData?.subscription_items?.find(
                  (item) => item.itemType === "plan"
                );
                const planPriceId = planItem?.itemPriceId ?? "";
                const billingUnit = subscriptionData?.subscription?.billingPeriodUnit;
                MixpanelService.track("ua_subscription_cancel_clicked", {
                  subscription_id: subscriptionData?.subscription?.id ?? "",
                  plan: planPriceId ? formatPlanName(planPriceId) : "",
                  billing_cycle:
                    billingUnit === "year"
                      ? "Yearly"
                      : billingUnit === "month"
                        ? "Monthly"
                        : billingUnit || "",
                  next_billing_date: formatDate(
                    subscriptionData?.subscription?.nextBillingAt
                  ),
                });
                setCancelSubscriptionModalOpen(true);
              }}
            >
              Cancel Subscription
            </Button>
          )}
        <CancellationFlow
          open={cancelSubscriptionModal}
          onClose={() => setCancelSubscriptionModalOpen(false)}
          navigate={navigate}
          subscriptionId={subscriptionData?.subscription?.id}
          subscriptionItems={subscriptionData?.subscription_items}
          cancellationSnapshot={{
            totalAmount:
              subscriptionData?.subscription_items?.reduce((sum, item) => sum + (item.amount || 0), 0) ?? 0,
            planId:
              subscriptionData?.subscription_items?.find((item) => item.itemType === "plan")?.itemPriceId || "",
            subscriptionStatus: subscriptionData?.subscription?.status || "",
            startedAt: subscriptionData?.subscription?.startedAt || 0,
          }}
        />
        <CancelSubscription
          open={cancelAddonModal}
          onClose={() => setCancelAddonModal(false)}
          onConfirm={() => {
            if (selectedAddonToCancel) {
              handleCancelAddon(selectedAddonToCancel);
            }
          }}
          addonName={selectedAddonToCancel ? formatPlanName(selectedAddonToCancel) : ""}
        />
      </Box>
      {/* ===================footer-section ================ */}

      <BillingCycleModal
        open={isBillingCycleModalOpen}
        onClose={() => setIsBillingCycleModalOpen(false)}
        onConfirm={handleBillingCycleConfirm}
        proRatedAmount={50}
        annualAmount={490}
      />
    </Container>
  );
};

export default SubscriptionDetails;
